import React, { PropsWithChildren, createContext, useEffect, useState, useCallback, useMemo } from 'react';
import i18n, { SupportedLanguage, supportedLanguages, isRtlLanguage, languageNames } from '../i18n';

type LanguageContextType = {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  direction: 'ltr' | 'rtl';
  languageNames: Record<SupportedLanguage, string>;
  supportedLanguages: readonly SupportedLanguage[];
};

export const LanguageContext = createContext<LanguageContextType>({} as LanguageContextType);

export const LanguageContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    // Initialize from localStorage or i18n detected language
    try {
      const savedLang = localStorage.getItem('language');
      if (savedLang && supportedLanguages.includes(savedLang as SupportedLanguage)) {
        return savedLang as SupportedLanguage;
      }
    } catch {
      // localStorage may not be available
    }
    // Fall back to i18n detected language or default to English
    const i18nLang = i18n.language?.split('-')[0] as SupportedLanguage;
    return supportedLanguages.includes(i18nLang) ? i18nLang : 'en';
  });

  // Compute direction based on language
  const direction = useMemo<'ltr' | 'rtl'>(() => (isRtlLanguage(language) ? 'rtl' : 'ltr'), [language]);

  // Sync language with i18n on mount and when language changes
  useEffect(() => {
    // Ensure i18n language matches context language
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [direction, language]);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    if (!supportedLanguages.includes(lang)) {
      return;
    }

    setLanguageState(lang);

    // Persist to localStorage
    try {
      localStorage.setItem('language', lang);
    } catch {
      // localStorage may not be available
    }

    // Update i18next language
    i18n.changeLanguage(lang);
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      direction,
      languageNames,
      supportedLanguages,
    }),
    [language, setLanguage, direction]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
